import React, { useState, useEffect } from 'react';
import { veoCreditAdminService, VeoCreditUserDetails } from '../../services/veoCreditAdminService';
import styles from './FuturisticAdmin.module.css';

interface VeoCreditUserDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  userEmail: string;
}

const VeoCreditUserDetailsModal: React.FC<VeoCreditUserDetailsModalProps> = ({
  isOpen,
  onClose,
  userId,
  userEmail,
}) => {
  const [userDetails, setUserDetails] = useState<VeoCreditUserDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && userId) {
      loadUserDetails();
    }
  }, [isOpen, userId]);

  const loadUserDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const details = await veoCreditAdminService.getUserVeoCredits(userId);
      setUserDetails(details);
    } catch (err) {
      console.error('Failed to load user details:', err);
      setError('Failed to load user VEO credit details');
    } finally {
      setLoading(false);
    }
  };

  const formatTimestamp = (timestamp: string): string => {
    return new Date(timestamp).toLocaleString();
  };

  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      backdropFilter: 'blur(8px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '2rem'
    }}>
      <div style={{
        backgroundColor: 'rgba(15, 23, 42, 0.95)',
        border: '1px solid rgba(51, 65, 85, 0.5)',
        borderRadius: '16px',
        maxWidth: '900px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'hidden',
        position: 'relative'
      }}>
        {/* Modal Header */}
        <div style={{
          padding: '2rem',
          borderBottom: '1px solid rgba(51, 65, 85, 0.5)',
          background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.1), rgba(139, 92, 246, 0.1))'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h2 style={{ 
                color: 'white', 
                fontSize: '1.5rem', 
                fontWeight: 'bold', 
                margin: '0 0 0.5rem 0',
                background: 'linear-gradient(135deg, #06b6d4, #8b5cf6)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                🎬 VEO Credit Details
              </h2>
              <p style={{ color: '#94a3b8', margin: 0, fontSize: '1rem' }}>
                {userEmail}
              </p>
            </div>
            <button
              onClick={onClose}
              style={{
                backgroundColor: 'rgba(239, 68, 68, 0.2)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                color: '#ef4444',
                padding: '0.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1.2rem',
                width: '40px',
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ×
            </button>
          </div>
        </div>

        {/* Modal Content */}
        <div style={{ 
          maxHeight: 'calc(90vh - 140px)', 
          overflowY: 'auto',
          padding: '0'
        }}>
          {loading ? (
            <div style={{ 
              padding: '4rem', 
              textAlign: 'center', 
              color: 'white' 
            }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔄</div>
              <h3>Loading user details...</h3>
            </div>
          ) : error ? (
            <div style={{ 
              padding: '4rem', 
              textAlign: 'center', 
              color: '#ef4444' 
            }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>❌</div>
              <h3>{error}</h3>
              <button 
                onClick={loadUserDetails}
                style={{
                  backgroundColor: 'rgba(239, 68, 68, 0.2)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  color: '#ef4444',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  marginTop: '1rem'
                }}
              >
                Retry
              </button>
            </div>
          ) : userDetails ? (
            <div style={{ padding: '2rem' }}>
              {/* User Info Section */}
              <div style={{
                backgroundColor: 'rgba(51, 65, 85, 0.3)',
                border: '1px solid rgba(51, 65, 85, 0.5)',
                borderRadius: '12px',
                padding: '1.5rem',
                marginBottom: '2rem'
              }}>
                <h3 style={{ 
                  color: '#06b6d4', 
                  fontSize: '1.25rem', 
                  margin: '0 0 1rem 0' 
                }}>
                  Account Information
                </h3>
                
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                  gap: '1rem' 
                }}>
                  <div>
                    <div style={{ color: '#94a3b8', fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                      Email
                    </div>
                    <div style={{ color: 'white', fontWeight: '500' }}>
                      {userDetails.user.email}
                    </div>
                  </div>
                  
                  <div>
                    <div style={{ color: '#94a3b8', fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                      Plan
                    </div>
                    <div style={{ 
                      color: '#06b6d4', 
                      fontWeight: '500',
                      backgroundColor: 'rgba(6, 182, 212, 0.1)',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px',
                      display: 'inline-block',
                      fontSize: '0.875rem'
                    }}>
                      {userDetails.user.plan.replace('_', ' ').toUpperCase()}
                    </div>
                  </div>
                  
                  <div>
                    <div style={{ color: '#94a3b8', fontSize: '0.875rem', marginBottom: '0.25rem' }}>
                      Role
                    </div>
                    <div style={{ color: 'white', fontWeight: '500' }}>
                      {userDetails.user.role}
                    </div>
                  </div>
                </div>
              </div>

              {/* Credit Balance Section */}
              <div style={{
                backgroundColor: 'rgba(16, 185, 129, 0.1)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                borderRadius: '12px',
                padding: '1.5rem',
                marginBottom: '2rem'
              }}>
                <h3 style={{ 
                  color: '#10b981', 
                  fontSize: '1.25rem', 
                  margin: '0 0 1rem 0' 
                }}>
                  Credit Balance
                </h3>
                
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
                  gap: '1rem' 
                }}>
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ 
                      fontSize: '2rem', 
                      fontWeight: 'bold', 
                      color: userDetails.balance.credits > 0 ? '#10b981' : '#ef4444',
                      marginBottom: '0.25rem' 
                    }}>
                      {veoCreditAdminService.formatCredits(userDetails.balance.credits)}
                    </div>
                    <div style={{ color: '#94a3b8', fontSize: '0.875rem' }}>
                      Current Credits
                    </div>
                  </div>
                  
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ 
                      fontSize: '1.5rem', 
                      fontWeight: 'bold', 
                      color: 'white',
                      marginBottom: '0.25rem' 
                    }}>
                      {veoCreditAdminService.formatCredits(userDetails.balance.totalPurchased)}
                    </div>
                    <div style={{ color: '#94a3b8', fontSize: '0.875rem' }}>
                      Total Purchased
                    </div>
                  </div>
                  
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ 
                      fontSize: '1.5rem', 
                      fontWeight: 'bold', 
                      color: '#06b6d4',
                      marginBottom: '0.25rem' 
                    }}>
                      {veoCreditAdminService.formatCredits(userDetails.balance.totalConsumed)}
                    </div>
                    <div style={{ color: '#94a3b8', fontSize: '0.875rem' }}>
                      Total Consumed
                    </div>
                  </div>
                  
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ 
                      fontSize: '1.5rem', 
                      fontWeight: 'bold', 
                      color: '#10b981',
                      marginBottom: '0.25rem' 
                    }}>
                      {veoCreditAdminService.formatCurrency(userDetails.balance.totalSpent)}
                    </div>
                    <div style={{ color: '#94a3b8', fontSize: '0.875rem' }}>
                      Total Spent
                    </div>
                  </div>
                </div>
                
                {/* Utilization Rate */}
                <div style={{ 
                  marginTop: '1rem', 
                  textAlign: 'center',
                  padding: '1rem',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '8px'
                }}>
                  <div style={{ color: '#94a3b8', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                    Credit Utilization Rate
                  </div>
                  <div style={{ 
                    fontSize: '1.25rem', 
                    fontWeight: 'bold', 
                    color: '#8b5cf6' 
                  }}>
                    {veoCreditAdminService.calculateUtilizationRate(
                      userDetails.balance.totalPurchased, 
                      userDetails.balance.credits
                    )}%
                  </div>
                </div>
              </div>

              {/* Transaction History */}
              <div style={{
                backgroundColor: 'rgba(139, 92, 246, 0.1)',
                border: '1px solid rgba(139, 92, 246, 0.3)',
                borderRadius: '12px',
                padding: '0',
                overflow: 'hidden'
              }}>
                <div style={{ 
                  padding: '1.5rem',
                  borderBottom: '1px solid rgba(139, 92, 246, 0.3)'
                }}>
                  <h3 style={{ 
                    color: '#8b5cf6', 
                    fontSize: '1.25rem', 
                    margin: 0 
                  }}>
                    Transaction History ({userDetails.transactions.length})
                  </h3>
                </div>
                
                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                  {userDetails.transactions.length > 0 ? (
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead style={{ 
                        backgroundColor: 'rgba(139, 92, 246, 0.1)',
                        position: 'sticky',
                        top: 0
                      }}>
                        <tr>
                          <th style={{ 
                            textAlign: 'left', 
                            padding: '1rem', 
                            color: '#8b5cf6', 
                            fontWeight: '500',
                            fontSize: '0.875rem'
                          }}>
                            Type
                          </th>
                          <th style={{ 
                            textAlign: 'right', 
                            padding: '1rem', 
                            color: '#8b5cf6', 
                            fontWeight: '500',
                            fontSize: '0.875rem'
                          }}>
                            Credits
                          </th>
                          <th style={{ 
                            textAlign: 'right', 
                            padding: '1rem', 
                            color: '#8b5cf6', 
                            fontWeight: '500',
                            fontSize: '0.875rem'
                          }}>
                            Amount
                          </th>
                          <th style={{ 
                            textAlign: 'left', 
                            padding: '1rem', 
                            color: '#8b5cf6', 
                            fontWeight: '500',
                            fontSize: '0.875rem'
                          }}>
                            Description
                          </th>
                          <th style={{ 
                            textAlign: 'left', 
                            padding: '1rem', 
                            color: '#8b5cf6', 
                            fontWeight: '500',
                            fontSize: '0.875rem'
                          }}>
                            Date
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {userDetails.transactions.map((transaction, index) => {
                          const typeInfo = veoCreditAdminService.getTransactionTypeDisplay(transaction.type);
                          
                          return (
                            <tr 
                              key={transaction.id}
                              style={{ 
                                borderTop: index > 0 ? '1px solid rgba(139, 92, 246, 0.2)' : 'none' 
                              }}
                            >
                              <td style={{ padding: '1rem' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                  <span style={{ fontSize: '1.2rem' }}>{typeInfo.icon}</span>
                                  <div>
                                    <div style={{ 
                                      color: typeInfo.color, 
                                      fontWeight: '500', 
                                      fontSize: '0.875rem' 
                                    }}>
                                      {typeInfo.label}
                                    </div>
                                    {transaction.veoModel && (
                                      <div style={{ 
                                        color: '#64748b', 
                                        fontSize: '0.75rem' 
                                      }}>
                                        {transaction.veoModel}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </td>
                              <td style={{ 
                                padding: '1rem', 
                                textAlign: 'right',
                                color: transaction.credits > 0 ? '#10b981' : '#ef4444',
                                fontWeight: '600'
                              }}>
                                {transaction.credits > 0 ? '+' : ''}{veoCreditAdminService.formatCredits(transaction.credits)}
                              </td>
                              <td style={{ 
                                padding: '1rem', 
                                textAlign: 'right',
                                color: '#94a3b8'
                              }}>
                                {transaction.amount ? veoCreditAdminService.formatCurrency(transaction.amount) : '-'}
                              </td>
                              <td style={{ 
                                padding: '1rem',
                                color: '#e2e8f0',
                                fontSize: '0.875rem',
                                maxWidth: '200px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {transaction.description}
                              </td>
                              <td style={{ 
                                padding: '1rem',
                                color: '#94a3b8',
                                fontSize: '0.875rem'
                              }}>
                                {formatTimestamp(transaction.createdAt)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  ) : (
                    <div style={{ 
                      padding: '3rem', 
                      textAlign: 'center', 
                      color: '#64748b' 
                    }}>
                      <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>📝</div>
                      <div>No transactions found</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default VeoCreditUserDetailsModal;
